import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslation } from "react-i18next";
import {
  colorBorder,
  colorSurface,
  colorSurfaceHover,
  colorTextPrimary,
  colorTextSecondary,
  radiusInput,
} from "src/design/tokens";

/** A restaurant keeps at least one table — the board has no column to draw below that. */
export const MIN_TABLES = 1;

interface TableCountControlProps {
  numberOfTables: number;
  /** False when the caller may not change the count; both steppers go inert. */
  canUpdate: boolean;
  /** True while a change is in flight, so a burst of clicks cannot race the server. */
  isPending: boolean;
  onChange: (numberOfTables: number) => void;
  sx?: SxProps;
}

/**
 * The table count, with a stepper either side: minus drops the last column, plus
 * appends one. It sits beside the day navigation so the two things that shape the
 * board — which day, how many tables — are edited from the same band.
 *
 * Purely presentational: the caller owns the mutation and the refetch, so this
 * stays usable wherever the count needs editing.
 */
export const TableCountControl = ({
  numberOfTables,
  canUpdate,
  isPending,
  onChange,
  sx,
}: TableCountControlProps) => {
  const { t } = useTranslation();

  const stepperSx = {
    color: colorTextSecondary,
    p: 0.5,
    ":hover": { backgroundColor: colorSurfaceHover },
  } as const;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        border: `1px solid ${colorBorder}`,
        borderRadius: radiusInput,
        backgroundColor: colorSurface,
        ...sx,
      }}
    >
      {/* Wrapped: MUI drops the tooltip of a disabled button without a live host. */}
      <Tooltip title={t("tableSchedule.tableCount.removeHint")} arrow>
        <span>
          <IconButton
            size="small"
            aria-label="remove-table"
            disabled={!canUpdate || isPending || numberOfTables <= MIN_TABLES}
            onClick={() => onChange(numberOfTables - 1)}
            sx={stepperSx}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Typography
        sx={{
          px: 0.5,
          fontSize: { xs: 12, sm: 12.5 },
          fontWeight: 600,
          color: colorTextPrimary,
          whiteSpace: "nowrap",
        }}
      >
        {t("tableSchedule.tableCount.label", { count: numberOfTables })}
      </Typography>

      <Tooltip title={t("tableSchedule.tableCount.addHint")} arrow>
        <span>
          <IconButton
            size="small"
            aria-label="add-table"
            disabled={!canUpdate || isPending}
            onClick={() => onChange(numberOfTables + 1)}
            sx={stepperSx}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
